/**
 * Readings → the payload `WorkstationChart` draws.
 *
 * The demo Workstation is handed a chart already resolved server-side: one
 * shared sample grid, axes, rules, events, statistics. The live API hands over
 * raw readings — each tag on its own clock, oldest first — and nothing else. So
 * this does the one thing a plot needs and no more: it puts every reading on a
 * TIME axis (`times`, which the chart uses in place of index spacing) and leaves
 * a hole wherever a tag has no reading at an instant another tag does.
 *
 * Nothing is averaged, resampled or smoothed. No statistics, no thresholds, no
 * correlation insight — the demo screen computes those from authored data, and
 * the API serves none of them for a real chart. Absent rather than invented.
 */

/** Identity colours, in order — the same list the demo Workstation uses. */
const SERIES_TONES = ['forge', 'ack', 'violet', 'green', 'amber', 'watch']

/**
 * @param {object} input
 * @param {string} input.name
 * @param {Array<{id: string, name: string, unit: string}>} input.series  In chart order.
 * @param {Array<{tagId: string, at: string, value: number}>} input.readings
 * @param {{fromMs: number, toMs: number}} input.span
 */
export function toPlot({ name, series, readings, span }) {
  const stampSet = new Set()
  const byTag = new Map(series.map((entry) => [entry.id, new Map()]))
  for (const reading of readings) {
    const values = byTag.get(reading.tagId)
    if (!values) continue
    stampSet.add(reading.at)
    values.set(reading.at, reading.value)
  }

  const stamps = [...stampSet].sort((a, b) => Date.parse(a) - Date.parse(b))
  const times = stamps.map((stamp) => Date.parse(stamp))

  /* Units in the order their first series appears. The first is the left axis,
     the second the right; a third or later shares the right axis' scale only in
     overlay, which is why the screen opens such a chart stacked. */
  const units = []
  for (const entry of series) if (!units.includes(entry.unit)) units.push(entry.unit)

  const plotted = series.map((entry, index) => {
    const values = byTag.get(entry.id)
    return {
      id: entry.id,
      name: entry.name,
      unit: entry.unit,
      tone: SERIES_TONES[index % SERIES_TONES.length],
      axis: units.indexOf(entry.unit) === 0 ? 'left' : 'right',
      values: stamps.map((stamp) => (values.has(stamp) ? values.get(stamp) : null)),
      count: values.size,
    }
  })

  const domainOf = (axis) => {
    const values = plotted
      .filter((entry) => entry.axis === axis)
      .flatMap((entry) => entry.values)
      .filter((value) => value != null)
    if (!values.length) return null
    const min = Math.min(...values)
    const max = Math.max(...values)
    const pad = (max - min) * 0.08 || Math.abs(max) * 0.08 || 1
    return [min - pad, max + pad]
  }

  const label = (list) => list.filter(Boolean).join(' · ')
  const left = domainOf('left')
  const right = domainOf('right')

  return {
    name,
    stamps,
    times,
    timeDomain: [span.fromMs, span.toMs],
    span: { hours: (span.toMs - span.fromMs) / 3_600_000 },
    series: plotted,
    axes: {
      left: { domain: left ?? [0, 1], label: label([units[0]]) },
      ...(right ? { right: { domain: right, label: label(units.slice(1)) } } : {}),
    },
    rules: [],
    events: { maintenance: [], anomalies: [] },
    unitCount: units.length,
  }
}
